import {
  isUncertainChoice,
  type ScriptChoice,
  type ScriptConversion,
} from "@kensio/pinyinjs";

import { element } from "./dom.js";

/**
 * Turning a script conversion into what the visitor sees.
 *
 * The characters the conversion had to guess at are marked, in the way the
 * pinyin tool marks a reading it was unsure of. Only the guesses: a character
 * settled by the word it was in, or by how that word is read, is not a doubt
 * and marking it as one would throw away the reason this converter is more
 * accurate than a lookup table. Those are listed under the output instead, by
 * `script-choices.ts`.
 */

/** The class marking a character the conversion was guessing at. */
const uncertainClass = "cb-uncertain";

/**
 * The other forms a character could have taken, written out.
 */
export function alternativesNote(choice: ScriptChoice): string | undefined {
  return choice.alternatives.length === 0
    ? undefined
    : `Could also be ${choice.alternatives.join(" or ")}`;
}

/**
 * One converted character as a node, marked if it was a guess.
 */
function choiceNode(choice: ScriptChoice): Node {
  if (!isUncertainChoice(choice)) {
    return document.createTextNode(choice.to);
  }

  return element("span", {
    text: choice.to,
    className: uncertainClass,
    attributes: { title: alternativesNote(choice) },
  });
}

/**
 * A whole conversion as nodes, ready to go into the output panel.
 *
 * The choices account for the Han characters and nothing else, so the text
 * between them is found by walking the converted text alongside them rather
 * than by assuming the two line up. Searching forward from where the last
 * choice ended is safe because a choice is always written as Han and the text
 * in between never is, so nothing between two choices can be mistaken for one.
 */
export function conversionNodes(conversion: ScriptConversion): readonly Node[] {
  const nodes: Node[] = [];
  let at = 0;

  for (const choice of conversion.choices) {
    const found = conversion.text.indexOf(choice.to, at);
    if (found === -1) {
      break;
    }

    if (found > at) {
      nodes.push(document.createTextNode(conversion.text.slice(at, found)));
    }

    nodes.push(choiceNode(choice));
    at = found + choice.to.length;
  }

  if (at < conversion.text.length) {
    nodes.push(document.createTextNode(conversion.text.slice(at)));
  }

  return nodes;
}
